import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { invalidatePlannerTasks } from '../hooks/invalidatePlannerTasks';
import { parseISO } from 'date-fns';

import {
  createAbsenceFromProposal,
  createAbsenceUpdateDuties,
  getDefaultSettings,
  getAbsenceRollout,
  getLeaveRequestDetails,
  getRegisterAbsenceDutyHourTypes,
  getRegisterAbsenceDuties,
  getRegisterAbsenceProposal,
  getRegisterAbsenceReasons,
  type RegisterAbsenceProposalEntry
} from 'RestAPIs/roster/register-absence';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import showError from 'components/shared/showError';

import {
  DEFAULT_REASON_KEY,
  type WorkAgreementSetting,
  createInitialWorkAgreementSetting,
  isSingleDayRange,
  serializeWorkAgreementSettings
} from './LeaveRequestProposalDialog.utils';

const BPM_TASK_COMPLETION_DELAY_MS = 800;

interface UseLeaveRequestProposalOptions {
  open: boolean;
  leaveRequestId: string;
  onClose: () => void;
}

const useLeaveRequestProposal = ({
  open,
  leaveRequestId,
  onClose
}: UseLeaveRequestProposalOptions) => {
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [proposals, setProposals] = useState<RegisterAbsenceProposalEntry[]>(
    []
  );
  const [editingDutyIndex, setEditingDutyIndex] = useState<number | null>(null);
  const [workAgreementSettings, setWorkAgreementSettings] = useState<
    Record<number, WorkAgreementSetting>
  >({});
  const trimmedLeaveRequestId = leaveRequestId.trim();
  const parsedLeaveRequestId = /^\d+$/.test(trimmedLeaveRequestId)
    ? Number.parseInt(trimmedLeaveRequestId, 10)
    : Number.NaN;
  const hasValidLeaveRequestId = Number.isFinite(parsedLeaveRequestId);

  useEffect(() => {
    if (!open || hasValidLeaveRequestId) {
      return;
    }

    showErrorSnackbar('Invalid leave request id');
    onClose();
  }, [open, hasValidLeaveRequestId, onClose]);

  const leaveRequestQuery = useQuery(
    ['leaveRequestDetails', leaveRequestId],
    async () => {
      const { data } = await getLeaveRequestDetails(parsedLeaveRequestId);
      return data;
    },
    {
      enabled: open && hasValidLeaveRequestId
    }
  );

  const reasonsQuery = useQuery(
    ['registerAbsenceReasons'],
    async () => {
      const [{ data: reasons }, { data: settings }] = await Promise.all([
        getRegisterAbsenceReasons(),
        getDefaultSettings()
      ]);

      const defaultReasonId = Number(
        settings.find((setting) => setting.key === DEFAULT_REASON_KEY)?.value
      );
      const defaultReasonCode = reasons.find(
        (reason) => reason.id === defaultReasonId
      )?.code;

      return {
        reasons,
        defaultReasonCode: defaultReasonCode ?? reasons[0]?.code ?? ''
      };
    },
    {
      enabled: open
    }
  );

  const workAgreementsQuery = useQuery(
    [
      'registerAbsenceWorkAgreements',
      leaveRequestQuery.data?.employeeId,
      leaveRequestQuery.data?.valid.from,
      leaveRequestQuery.data?.valid.to
    ],
    async () => {
      const { data } = await getRegisterAbsenceDuties({
        employeeId: leaveRequestQuery.data!.employeeId,
        startDate: leaveRequestQuery.data!.valid.from,
        endDate: leaveRequestQuery.data!.valid.to
      });

      return data.workAgreements;
    },
    {
      enabled: open && !!leaveRequestQuery.data
    }
  );

  const isSingleDayLeaveRequest = isSingleDayRange(
    leaveRequestQuery.data?.valid.from,
    leaveRequestQuery.data?.valid.to
  );

  const proposalQuery = useQuery(
    [
      'registerAbsenceProposal',
      leaveRequestQuery.data?.employeeId,
      leaveRequestQuery.data?.waId,
      leaveRequestQuery.data?.dutyTypeId,
      leaveRequestQuery.data?.valid.from,
      leaveRequestQuery.data?.valid.to
    ],
    async () => {
      const { data } = await getRegisterAbsenceProposal({
        employeeId: leaveRequestQuery.data!.employeeId,
        workAgreementId: leaveRequestQuery.data!.waId,
        dutyTypeId: leaveRequestQuery.data!.dutyTypeId,
        startDateTime: leaveRequestQuery.data!.valid.from,
        endDateTime: leaveRequestQuery.data!.valid.to
      });
      return data;
    },
    {
      enabled: open && !!leaveRequestQuery.data && isSingleDayLeaveRequest
    }
  );

  const dutyHourTypesQuery = useQuery(
    [
      'registerAbsenceDutyHourTypes',
      leaveRequestQuery.data?.valid.from,
      leaveRequestQuery.data?.valid.to
    ],
    async () => {
      const { data } = await getRegisterAbsenceDutyHourTypes({
        startDate: leaveRequestQuery.data!.valid.from,
        endDate: leaveRequestQuery.data!.valid.to
      });

      return data;
    },
    {
      enabled: open && !!leaveRequestQuery.data && !isSingleDayLeaveRequest
    }
  );

  const requestedWorkAgreementId = leaveRequestQuery.data?.waId;
  const requestedWorkAgreement = (workAgreementsQuery.data ?? []).find(
    (item) => item.waId === requestedWorkAgreementId
  );
  const requestedWorkAgreementSetting = requestedWorkAgreement
    ? workAgreementSettings[requestedWorkAgreement.waId] ??
      createInitialWorkAgreementSetting(requestedWorkAgreement)
    : null;

  const rolloutQuery = useQuery(
    [
      'registerAbsenceRollout',
      leaveRequestQuery.data?.employeeId,
      leaveRequestQuery.data?.dutyTypeId,
      leaveRequestQuery.data?.valid.from,
      leaveRequestQuery.data?.valid.to,
      requestedWorkAgreementId && requestedWorkAgreementSetting
        ? serializeWorkAgreementSettings([requestedWorkAgreementId], {
            [requestedWorkAgreementId]: requestedWorkAgreementSetting
          })
        : []
    ],
    async () => {
      if (!requestedWorkAgreement || !requestedWorkAgreementSetting) {
        return {
          existingDutyLines: [],
          fteRolloutDutyLines: []
        };
      }

      const { data } = await getAbsenceRollout({
        employeeId: leaveRequestQuery.data!.employeeId,
        dutyTypeId: leaveRequestQuery.data!.dutyTypeId,
        startDate: leaveRequestQuery.data!.valid.from,
        endDate: leaveRequestQuery.data!.valid.to,
        absencePercentage: 100,
        workAgreementArguments: [
          {
            workAgreementId: requestedWorkAgreement.waId,
            inputDays: requestedWorkAgreementSetting.rolloutStartDate
              ? requestedWorkAgreementSetting.inputDays
              : [],
            rolloutStartDate: requestedWorkAgreementSetting.rolloutStartDate
              ? parseISO(
                  requestedWorkAgreementSetting.rolloutStartDate
                ).toISOString()
              : null,
            inputWeekHours:
              requestedWorkAgreement.hoursPerWeek ??
              requestedWorkAgreement.weekHoursMin
          }
        ]
      });

      return data;
    },
    {
      enabled:
        open &&
        !!leaveRequestQuery.data &&
        !isSingleDayLeaveRequest &&
        !!requestedWorkAgreement,
      keepPreviousData: true
    }
  );

  const queryClient = useQueryClient();

  // CreateAbsenceOperationImpl.execute() fires LEAVE_REQUEST_ABSENCE_CREATED at
  // the event bus without awaiting the reply, so the absence POST returns before
  // BPM has completed the leave task and an immediate refetch still returns it.
  // The delay is a bound on that gap, not a guarantee: if BPM is slower the task
  // reappears until the next invalidation (tab switch or the sync button).
  const handleAbsenceSaved = () => {
    showSuccessSnackbar();
    onClose();
    setTimeout(() => {
      invalidatePlannerTasks(queryClient);
    }, BPM_TASK_COMPLETION_DELAY_MS);
  };

  const saveMutation = useMutation(createAbsenceFromProposal, {
    onSuccess: handleAbsenceSaved,
    onError: showError
  });

  const saveRolloutMutation = useMutation(createAbsenceUpdateDuties, {
    onSuccess: handleAbsenceSaved,
    onError: showError
  });

  useEffect(() => {
    if (reasonsQuery.data?.defaultReasonCode) {
      setReasonCode(reasonsQuery.data.defaultReasonCode);
    }
  }, [reasonsQuery.data]);

  useEffect(() => {
    setProposals(proposalQuery.data ?? []);
  }, [proposalQuery.data]);

  useEffect(() => {
    setNote(
      leaveRequestQuery.data?.empNote ?? leaveRequestQuery.data?.note ?? ''
    );
  }, [leaveRequestQuery.data]);

  useEffect(() => {
    if (requestedWorkAgreement) {
      setWorkAgreementSettings((current) => ({
        ...current,
        [requestedWorkAgreement.waId]:
          current[requestedWorkAgreement.waId] ??
          createInitialWorkAgreementSetting(requestedWorkAgreement)
      }));
    }
  }, [requestedWorkAgreement]);

  useEffect(() => {
    if (
      open &&
      (leaveRequestQuery.isError ||
        reasonsQuery.isError ||
        workAgreementsQuery.isError ||
        proposalQuery.isError ||
        dutyHourTypesQuery.isError ||
        rolloutQuery.isError)
    ) {
      showErrorSnackbar();
      onClose();
    }
  }, [
    leaveRequestQuery.isError,
    onClose,
    open,
    dutyHourTypesQuery.isError,
    proposalQuery.isError,
    reasonsQuery.isError,
    rolloutQuery.isError,
    workAgreementsQuery.isError
  ]);

  const isLoading =
    leaveRequestQuery.isLoading ||
    reasonsQuery.isLoading ||
    workAgreementsQuery.isLoading ||
    (!isSingleDayLeaveRequest && dutyHourTypesQuery.isLoading) ||
    (isSingleDayLeaveRequest
      ? proposalQuery.isLoading && !proposalQuery.data
      : rolloutQuery.isLoading && !rolloutQuery.data);

  const leaveRequest = leaveRequestQuery.data;
  const reasons = reasonsQuery.data?.reasons ?? [];
  const selectedWorkAgreements = requestedWorkAgreement
    ? [requestedWorkAgreement]
    : [];
  const selectedDutyHourType = dutyHourTypesQuery.data?.find(
    (item) => item.dutyTypeId === leaveRequest?.dutyTypeId
  );
  const rolloutData = rolloutQuery.data;
  const existingRolloutDuties = rolloutData?.existingDutyLines ?? [];
  const newRolloutDuties = rolloutData?.fteRolloutDutyLines ?? [];

  const updateWorkAgreementSetting = (
    workAgreementId: number,
    partial: Partial<WorkAgreementSetting>
  ) => {
    setWorkAgreementSettings((current) => ({
      ...current,
      [workAgreementId]: {
        ...(current[workAgreementId] ?? {
          rolloutStartDate: null,
          inputDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        }),
        ...partial
      }
    }));
  };

  return {
    reasonCode,
    setReasonCode,
    note,
    proposals,
    setProposals,
    editingDutyIndex,
    setEditingDutyIndex,
    workAgreementSettings,
    updateWorkAgreementSetting,
    isSingleDayLeaveRequest,
    isLoading,
    leaveRequest,
    reasons,
    selectedWorkAgreements,
    requestedWorkAgreement,
    selectedDutyHourType,
    existingRolloutDuties,
    newRolloutDuties,
    saveMutation,
    saveRolloutMutation
  };
};

export default useLeaveRequestProposal;
